<template>
  <v-row no-gutters>
    <v-col class="pt-0">
      <sidebar-row v-if="!hideDivider">
        <v-divider></v-divider>
      </sidebar-row>
      <sidebar-row :has-icon-column="false">
        <v-tabs v-model="currentTab" hide-slider class="custom-tabs pb-4 pt-4" :class="{ 'pt-0': hideDivider }">
          <v-tooltip bottom color="font" v-for="tab in computedTabs" :key="tab.value" :disabled="tab.isTooltipDisabled">
            <template #activator="{ on }">
              <div v-on="on" class="extra-div">
                <v-tab v-text="tab.text" :disabled="tab.isTabDisabled" active-class="activeTab"> </v-tab>
              </div>
            </template>
            {{ tab.tooltipText }}
          </v-tooltip>
        </v-tabs>
      </sidebar-row>
      <v-row no-gutters>
        <v-col class="pr-3">
          <v-tabs-items v-model="currentTab">
            <v-tab-item v-for="tab in computedTabs" :key="tab.value">
              <component
                :is="tab.component"
                :dataSourceName="dataSourceName"
                :subDataSourceName="subDataSourceName"
                :featureId="featureId"
                :subFeatureId="subFeatureId"
                :init-function="tab.initFunction"
                v-bind="tab.additionalProps"
              />
            </v-tab-item>
          </v-tabs-items>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
import fetchAttributesSchemaMixin from '@/mixins/fetchAttributesSchemaMixin';
import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';
import { call, get } from 'vuex-pathify';
import Notes from '@/components/Notes';
import ChangeHistory from '@/components/ChangeHistory';
import Attachments from '@/components/Attachments';
import SidebarRow from '@/components/SidebarRow';
import Relations from '@/components/Relations';
import Suggestions from '@/components/Suggestions';

export default {
  name: 'ObjectExtras',
  components: { SidebarRow, Attachments, ChangeHistory, Notes, Relations, Suggestions },
  mixins: [fetchAttributesSchemaMixin, prepareAttributesFeatureCardMixin],
  props: {
    layerId: {
      type: [String, Number],
      required: true,
    },
    featureId: {
      type: [String, Number],
      required: true,
    },
    subFeatureId: {
      type: [String, Number],
    },
    dataSourceName: {
      type: String,
      required: true,
    },
    subDataSourceName: {
      type: String,
    },
    permissions: {
      type: Number,
      required: false,
    },
    hideDivider: {
      type: Boolean,
      default: false,
    },
    tabs: {
      type: Array,
      default: () => {
        return ['changeHistory', 'notes', 'attachments', 'relations', 'suggestions'];
      },
      validator: value => {
        return value.every(v => ['changeHistory', 'notes', 'attachments', 'relations', 'suggestions'].includes(v));
      },
    },
    geometry: {
      type: Object,
      default: null,
    },
  },
  data: () => ({
    attachmentsFormSchema: [],
    currentTab: 0,
    relationsAttachmentsFetched: false,
  }),
  computed: {
    additionalModules: get('admin/additionalModules'),
    settings: get('admin/settingsValues'),
    toolsPermissions: get('users/toolsPermissions'),
    attachmentsAttributesMetadata: get('layers/metadata@:documentationDataSourceName.attributes_schema.attributes'),
    attachmentsLayerFormSchema: get('layers/layers@:documentationLayerId@form_schema.elements'),
    documentationDataSourceName: get('admin/modulesMapping@documentation.attachment_datasource_name'),
    documentationLayerId: get('admin/modulesMapping@documentation.documentation_layer_id'),
    isSuggestionsModuleEnabled: get('admin/additionalModules@UPDATE_SUGGESTIONS_MODULE.enabled'),
    computedTabs() {
      return this.computedValues.map(item => {
        return {
          ...item,
          tooltipText: this.tooltipText(item.value),
          isTabDisabled: this.isDisabled(item.value),
          isTooltipDisabled: !this.isDisabled(item.value),
        };
      });
    },
    computedValues() {
      const dict = {
        attachments: {
          text: this.$i18n.t('sidebar.attachments'),
          value: 'attachments',
          component: 'Attachments',
          initFunction: this.getRelationsWithAttachments,
          additionalProps: {
            formSchema: this.attachmentsFormSchema,
            expanded: this.hasExpandedAttachments,
            layerId: this.layerId,
          },
        },
        changeHistory: {
          text: this.$i18n.t('sidebar.changeHistory'),
          value: 'changeHistory',
          component: 'ChangeHistory',
          additionalProps: {
            layerId: this.layerId,
          },
        },
        notes: { text: this.$i18n.t('sidebar.notes'), value: 'notes', component: 'Notes' },
        relations: {
          text: this.$i18n.t('sidebar.relations'),
          value: 'relations',
          component: 'Relations',
          initFunction: this.getRelationsWithAttachments,
        },
        ...(this.isSuggestionsModuleVisible
          ? {
              suggestions: {
                text: this.$i18n.t('sidebar.suggestions'),
                value: 'suggestions',
                component: 'Suggestions',
                additionalProps: {
                  geometry: this.geometry,
                },
              },
            }
          : {}),
      };
      return this.tabs.map(tab => dict[tab]).filter(tab => !!tab);
    },
    isSuggestionsModuleVisible() {
      return (
        this.isSuggestionsModuleEnabled &&
        !!this.metadata[this.dataSourceName].geometry_type &&
        (!this.subDataSourceName || this.subDataSourceName === this.dataSourceName)
      );
    },
    hasExpandedAttachments() {
      const isModuleEnabled = this.additionalModules['DOCUMENTATION']?.enabled;
      const isSettingEnabled = this.settings['documentation_module_enabled'];
      const hasPermission = this.toolsPermissions['DOCUMENTATION']?.main_value > 0;
      return isModuleEnabled && isSettingEnabled && hasPermission;
    },
  },
  methods: {
    getRelations: call('relations/getRelations'),
    async getRelationsWithAttachments(payload, init = false) {
      if (this.relationsAttachmentsFetched && init) {
        return;
      }
      await this.getRelations(payload);
      if (this.hasExpandedAttachments) {
        await this.getRelationalValues(this.attachmentsLayerFormSchema, this.attachmentsAttributesMetadata);
        this.attachmentsFormSchema = this.$_flattenStructure(
          this.getForm(this.attachmentsLayerFormSchema, this.attachmentsAttributesMetadata),
          'elements'
        );
      }
      this.relationsAttachmentsFetched = true;
    },
    setCurrentTab(tab) {
      // TODO: Fix po zmianach w przyciskach pod tytułami kart
      if (tab === 'history') {
        tab = 'changeHistory';
      }
      this.currentTab = this.tabs.indexOf(tab);
    },
    isDisabled(value) {
      if (value === 'changeHistory') {
        return false;
      }
      return this.permissions === 0;
    },
    tooltipText(name) {
      return this.$i18n.t(`sidebar.noPermission${this.$_capitalize(name)}`);
    },
  },
};
</script>

<style lang="scss" scoped>
/* CR - dla display jak i margin są specjalne klasy w Vuetify */
.extra-div {
  display: flex;
  height: auto;
  margin: 0 20px 0 0;
}
.v-tab {
  letter-spacing: 0.0178571429em !important;
  border-radius: 4px;
  padding: 8px;
  font-size: 12px;
  font-weight: 500;
  height: fit-content;
  text-transform: unset !important;
}
.activeTab {
  background-color: map-get($colors, 'primarybackground');
}
/* CR - do tworzenia borderów jest osobny mixin, więc tutaj powinien zostać użyty. Tak samo padding ma klasy w Vuetify*/
.custom-tabs {
  padding-top: 26px;
  position: sticky;
  z-index: 1;
}
.v-tab:not(.activeTab) {
  color: #5f6368 !important;
}
::v-deep {
  .v-item-group {
    height: auto !important;
  }
  .v-window {
    overflow: initial !important;
  }
  .v-slide-group__prev {
    min-width: 40px !important;
    max-width: 8.33% !important;
    display: flex !important;
    flex-basis: 8.33% !important;
  }
}
</style>
